/**
 * TEMP-H-003: Execution Timeline Component
 * 
 * Main timeline component with virtualization, filtering, and live updates.
 * Displays Events v2 timeline with performance optimizations.
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { List } from 'react-window';
import { 
  Filter, 
  RefreshCw, 
  ChevronDown,
  AlertCircle,
  Inbox,
  Download
} from 'lucide-react';
import type { TimelineFilters } from '@/types/execution-events';
import { useExecutionEvents } from '@/hooks/useExecutionEvents';
import { TimelineFilters as TimelineFiltersComponent } from './TimelineFilters';
import { EventRow } from './EventRow';
import { ControlErrorBoundary } from './ControlErrorBoundary';

interface ExecutionTimelineProps {
  executionId: string;
  className?: string;
  height?: number;
  compactMode?: boolean;
  showFilters?: boolean;
}

// Row height for virtualization
const ROW_HEIGHT = 120; // Base height, will adjust for expanded rows
const COMPACT_ROW_HEIGHT = 80;

interface VirtualRowProps {
  index: number;
  style: React.CSSProperties;
  data: {
    events: any[];
    compactMode: boolean;
    showArtifacts: boolean;
  };
}

const VirtualRow: React.FC<VirtualRowProps> = ({ index, style, data }) => {
  const { events, compactMode, showArtifacts } = data;
  const event = events[index];

  if (!event) return null;

  return (
    <div style={style}>
      <EventRow
        event={event}
        compact={compactMode}
        showArtifacts={showArtifacts}
        className="border-b border-gray-800"
      />
    </div>
  );
};

export function ExecutionTimeline({ 
  executionId,
  className = '',
  height = 600,
  compactMode = false,
  showFilters = true
}: ExecutionTimelineProps) {
  const [filters, setFilters] = useState<TimelineFilters>({});
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [groupByStage, setGroupByStage] = useState(false);
  const [showArtifacts, setShowArtifacts] = useState(true);
  const [internalCompactMode, setInternalCompactMode] = useState(compactMode);
  const listRef = useRef<any>(null);

  const {
    events,
    eventsByStage,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    refresh: refetch,
    totalEvents
  } = useExecutionEvents({
    executionId,
    ...filters,
    limit: 50,
    enabled: !!executionId,
    refetchInterval: 30000 // 30 seconds
  });

  // Prepare data for virtualization
  const virtualData = useMemo(() => ({
    events: groupByStage ? Array.from(eventsByStage.values()).flat() : events,
    compactMode: internalCompactMode,
    showArtifacts
  }), [events, eventsByStage, groupByStage, internalCompactMode, showArtifacts]);

  const rowHeight = internalCompactMode ? COMPACT_ROW_HEIGHT : ROW_HEIGHT;

  // Handle infinite scroll
  const handleScroll = useCallback(({ scrollOffset, scrollUpdateWasRequested }: any) => {
    if (scrollUpdateWasRequested) return;
    
    const threshold = 0.8;
    const maxScroll = Math.max(0, (virtualData.events.length * rowHeight) - height);
    
    const canLoadMore = hasNextPage && !isFetchingNextPage;
    if (scrollOffset >= maxScroll * threshold && canLoadMore) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, height, rowHeight, virtualData.events.length]);

  // Reset list when events change
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollToItem(0, 'start');
    }
  }, [filters]);

  const handleFiltersChange = useCallback((newFilters: TimelineFilters) => {
    setFilters(newFilters);
  }, []);

  const handleFiltersReset = useCallback(() => {
    setFilters({});
  }, []);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const exportTimeline = useCallback(() => {
    const dataStr = JSON.stringify(events, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `timeline-${executionId}-${new Date().toISOString()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    URL.revokeObjectURL(url);
  }, [events, executionId]);

  // Filter summary
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.stage?.length) count += filters.stage.length;
    if (filters.eventType?.length) count += filters.eventType.length;
    if (filters.persona?.length) count += filters.persona.length;
    if (filters.timeRange) count += 1;
    return count;
  }, [filters]);

  if (isError) {
    return (
      <ControlErrorBoundary>
        <div className={`bg-red-500/10 border border-red-500/20 rounded-lg p-6 ${className}`}>
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-red-300 mb-1">
                Timeline Error
              </h3>
              <p className="text-xs text-red-400 mb-3">
                {error?.message || 'Failed to load timeline events'}
              </p>
              <button
                onClick={handleRefresh}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-600/20 text-red-300 hover:bg-red-600/30 border border-red-600/50 rounded-md transition-colors"
              >
                <RefreshCw className="w-3 h-3" />
                Retry
              </button>
            </div>
          </div>
        </div>
      </ControlErrorBoundary>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <ChevronDown className="w-5 h-5 text-purple-400" />
            <h3 className="text-lg font-semibold text-white">Events Timeline</h3>
          </div>
          
          {totalEvents > 0 && (
            <span className="px-2 py-1 text-xs bg-gray-700 text-gray-300 rounded">
              {totalEvents} events
            </span>
          )}
          
          {activeFiltersCount > 0 && (
            <span className="px-2 py-1 text-xs bg-purple-500/20 text-purple-300 rounded">
              {activeFiltersCount} filters
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* View Options */}
          <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setInternalCompactMode(!internalCompactMode)}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                internalCompactMode 
                  ? 'bg-purple-600 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Compact
            </button>
            
            <button
              onClick={() => setGroupByStage(!groupByStage)}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                groupByStage 
                  ? 'bg-purple-600 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Group
            </button>
            
            <button
              onClick={() => setShowArtifacts(!showArtifacts)}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                showArtifacts 
                  ? 'bg-purple-600 text-white' 
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Artifacts
            </button>
          </div>

          {/* Actions */}
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh timeline"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>

          {showFilters && (
            <button
              onClick={() => setShowFiltersPanel(!showFiltersPanel)}
              className={`p-2 rounded-lg transition-colors ${
                showFiltersPanel 
                  ? 'bg-purple-600 text-white' 
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
              title="Toggle filters"
            >
              <Filter className="w-4 h-4" />
            </button>
          )}

          <button
            onClick={exportTimeline}
            disabled={events.length === 0}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            title="Export timeline"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex gap-4">
        {/* Filters Panel */}
        {showFilters && showFiltersPanel && (
          <div className="w-80 flex-shrink-0">
            <TimelineFiltersComponent
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onReset={handleFiltersReset}
            />
          </div>
        )}

        {/* Timeline Content */}
        <div className="flex-1 min-w-0">
          {events.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <Inbox className="w-12 h-12 mb-4" />
              <h4 className="text-lg font-medium mb-2">No Events Found</h4>
              <p className="text-sm text-center max-w-sm">
                {activeFiltersCount > 0 
                  ? 'No events match your current filters. Try adjusting your criteria.'
                  : 'No events have been recorded for this execution yet.'
                }
              </p>
              {activeFiltersCount > 0 && (
                <button
                  onClick={handleFiltersReset}
                  className="mt-4 px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                >
                  Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="bg-gray-900/50 rounded-lg border border-gray-700 overflow-hidden">
              {/* Loading overlay */}
              {isLoading && (
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center z-10">
                  <div className="flex items-center gap-2 bg-gray-800 px-4 py-2 rounded-lg">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span className="text-sm text-gray-300">Loading events...</span>
                  </div>
                </div>
              )}

              {/* Virtualized Timeline */}
              {/* @ts-ignore react-window type issue */}
              <List
                ref={listRef}
                height={height}
                itemCount={virtualData.events.length}
                itemSize={rowHeight}
                itemData={virtualData}
                onScroll={handleScroll}
                className="timeline-list"
              >
                {/* @ts-ignore react-window children type issue */}
                {VirtualRow}
              </List>

              {/* Load More Indicator */}
              {hasNextPage && !isFetchingNextPage && (
                <div className="border-t border-gray-800 p-4 text-center">
                  <button
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="flex items-center gap-2 mx-auto px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isFetchingNextPage ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Loading more...
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        Load more events
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Default export for compatibility
export default ExecutionTimeline;